import { Stack, Typography } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useCallback, useMemo } from "react";
import {
  AT_LEAST_ONE_NUMBER,
  AT_LEAST_ONE_SPECIAL_CHARACTER,
  PASSWORD_CASE_REGEX,
  PASSWORD_LENGTH_REGEX_15_50,
  PrimaryFormConfirmPassword,
  PrimaryFormPassword,
  PrimaryFormUserNameWithValidations,
  USER_NAME_REGEX,
} from "react-common-ui-library";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  ACCOUNT_SETUP_HEADING,
  CONFIRM_PASSWORD_FIELD_NAME,
  USERNAME_ALREADY_EXISTS,
  USER_NAME_FIELD_NAME,
} from "../../../Constants/AccountSetupConstants";
import {
  EMPTY_STRING,
  H2,
  SPACING_3_4,
  SPACING_3_5,
  SPACING_4_5,
  VARIANT_H4,
} from "../../../Constants/CommonConstants";
import {
  PASSWORD_VALIDATION_DEFAULT_RESPONSE,
  PROVIDER_ACCOUNT_SETUP_HEADER,
} from "../../../Constants/ProviderAndCertifierAccountCreationConstants";
import {
  SLICE_PROVIDER_ACCOUNT_CREATION,
  THUNK_POST_PROVIDER_USERNAME,
} from "../../../Constants/SliceAndThunkConstants";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageProviderAccountCreationSelector } from "../../../Selectors/ProviderAccountCreationSelector";
import { manageReferenceDataSelector } from "../../../Selectors/ReferenceDataSelector";
import { manageUtilityDataSelector } from "../../../Selectors/UtilityDataSelector";
import { fetchUserRoleByValue } from "../../../Services/CommonServices";
import { StyledAccountSetupStack } from "../../../Styles/AccountCreation/AccountSetup/AccountSetupStyles";
import {
  validatePasswordResponse,
  verifyUsernameResponse,
} from "./ProviderAccountSetupUtils";

/**
 * @description Provider Account Setup form input
 * @returns {ReactNode} Renders the username, password and confirm password input to the user to setup their account
 */
const ProviderAccountSetupForm = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { accountType } = useSelector((state) =>
    manageProviderAccountCreationSelector(state)
  );

  const { userRole } = useSelector((state) =>
    manageReferenceDataSelector(state)
  );

  const userRoleValue = useMemo(() => {
    return fetchUserRoleByValue(userRole, accountType);
  }, [accountType, userRole]);

  const confirmPasswordFieldValue = useWatch({
    name: CONFIRM_PASSWORD_FIELD_NAME,
  });

  const userNameValue = useWatch({
    name: USER_NAME_FIELD_NAME,
  });

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_PROVIDER_ACCOUNT_CREATION,
      THUNK_POST_PROVIDER_USERNAME
    )
  );

  const { description } = apiResponses;

  const { isLoading } = useSelector((state) =>
    manageUtilityDataSelector(state)
  );

  const userNameAsyncValidation = useCallback(
    async (value) => {
      if (USER_NAME_REGEX.test(value)) {
        return await verifyUsernameResponse(value, dispatch);
      }
      return true;
    },
    [dispatch]
  );

  const passwordAsyncValidation = useCallback(
    async (value) => {
      if (
        USER_NAME_REGEX.test(userNameValue) &&
        description !== USERNAME_ALREADY_EXISTS &&
        PASSWORD_LENGTH_REGEX_15_50.test(value) &&
        PASSWORD_CASE_REGEX.test(value) &&
        (AT_LEAST_ONE_NUMBER.test(value) ||
          AT_LEAST_ONE_SPECIAL_CHARACTER.test(value))
      ) {
        const { errorResponse, successResponse } =
          await validatePasswordResponse(
            value,
            dispatch,
            userNameValue,
            userRoleValue.valueText
          );
        return { errorResponse, successResponse };
      }
      return PASSWORD_VALIDATION_DEFAULT_RESPONSE;
    },
    [dispatch, userNameValue, description, userRoleValue]
  );

  return (
    <Stack gap={SPACING_3_5}>
      <Stack gap={SPACING_4_5}>
        <Typography
          component={H2}
          variant={VARIANT_H4}
          id={PROVIDER_ACCOUNT_SETUP_HEADER}
        >
          {intl.formatMessage({
            id: ACCOUNT_SETUP_HEADING,
          })}
        </Typography>
      </Stack>
      <StyledAccountSetupStack>
        <Stack gap={SPACING_3_4}>
          <PrimaryFormUserNameWithValidations
            fieldProps={{
              userNameAsyncValidation: userNameAsyncValidation,
              disabled: isLoading,
            }}
          />
          <PrimaryFormPassword
            fieldProps={{
              passwordAsyncValidation: passwordAsyncValidation,
              disabled: isLoading,
              isAdminOrProvider: true,
              deps: [
                !isEmpty(confirmPasswordFieldValue)
                  ? CONFIRM_PASSWORD_FIELD_NAME
                  : EMPTY_STRING,
              ],
            }}
          />
        </Stack>
        <PrimaryFormConfirmPassword />
      </StyledAccountSetupStack>
    </Stack>
  );
};

export default ProviderAccountSetupForm;
